"use client";

import { Typography } from "@/components/typography";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Image from "next/image";
import NextLink from "next/link";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { ProjectWithSlideshow } from "../projects-components-sharedwithstaticsite/types";

interface ProjectDetailProps {
    project: ProjectWithSlideshow;
    content: string | null; //  MDXRemoteSerializeResult;
    otherProjects: {
        id: string;
        title: string;
        featuredScreenshotIndex: number;
        // image: string
    }[];
}

export default function ProjectDetail({ project, content, otherProjects }: ProjectDetailProps) {
    const [activeTab, setActiveTab] = useState("overview");
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);

    return (
        <div className="container max-w-4xl py-8">
            <div className="mb-6">
                <NextLink href="/" passHref>
                    <Button variant="ghost" size="sm" className="mb-4">
                        <span className="icon-[tabler--arrow-left] size-4 mr-2" />
                        Back to Projects
                    </Button>
                </NextLink>

                <Typography variant="h1" className="mb-4">
                    {project.title}
                </Typography>

                <div className="flex flex-wrap gap-2 mb-6">
                    {(project.tags || []).map((tag) => (
                        <Badge key={tag} variant="outline" className="bg-primary/10">
                            {tag}
                        </Badge>
                    ))}
                </div>
            </div>

            <div className="relative rounded-lg overflow-hidden mb-10 aspect-video">
                {!isVideoPlaying && project.videoUrl ? (
                    <div className="relative">
                        <Image
                            src={`https://web-mediafraucloud-com.s3.amazonaws.com/media/projects/${project.id}_screenshot_${project.featuredScreenshotIndex}_square.webp`}
                            alt={project.title}
                            width={640}
                            height={640}
                            className="rounded-lg"
                            priority
                        />
                        <div
                            className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer"
                            onClick={() => setIsVideoPlaying(true)}
                        >
                            <div className="bg-black/70 p-6 rounded-full">
                                <span className="icon-[tabler--player-play-filled] size-16 text-white" />
                            </div>
                        </div>
                    </div>
                ) : project.videoUrl ? (
                    <video
                        src={`https://web-mediafraucloud-com.s3.amazonaws.com/media/projects/${project.videoUrl}`}
                        className="max-w-[640px] rounded-lg"
                        controls
                        autoPlay
                        controlsList="nodownload"
                        onEnded={() => setIsVideoPlaying(false)}
                    />
                ) : (
                    <Image
                        src={`https://web-mediafraucloud-com.s3.amazonaws.com/media/projects/${project.id}_screenshot_${project.featuredScreenshotIndex}_square.webp`}
                        alt={project.title}
                        width={640}
                        height={640}
                        className="rounded-lg"
                        priority
                    />
                )}
            </div>

            <Tabs defaultValue="overview" className="mb-10">
                <TabsList className="mb-6">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="technical">Technical Details</TabsTrigger>
                    {project.status.link && <TabsTrigger value="live">Live Demo</TabsTrigger>}
                </TabsList>

                <TabsContent value="overview" className="prose dark:prose-invert max-w-none">
                    {/* <MDXRemote {...content} /> */}
                    {content && content.length > 20 ? (
                        <ReactMarkdown>{content}</ReactMarkdown>
                    ) : (
                        <div>Coming soon: detailed description of this project, technologies used, etc.</div>
                    )}
                </TabsContent>

                <TabsContent value="technical">
                    <div className="space-y-6">
                        {/* <div>
                            <Typography variant="h3" className="mb-4">
                                Technologies Used
                            </Typography>
                            <div className="flex flex-wrap gap-3">
                                {(project.technologiesUsed || []).map((tech) => (
                                    <Badge key={tech} className="flex items-center gap-2 px-3 py-1.5">
                                        <span className={`i con - [simple -icons--${tech.toLowerCase()}] size-4`} />
                                        {tech}
                                    </Badge>
                                ))}
                            </div>
                        </div> */}

                        <div>
                            <Typography variant="h3" className="mb-2">
                                Key Features
                            </Typography>

                            <ul className="list-disc pl-6 space-y-2  mt-2">
                                <li>... will fill this in soon... </li>
                            </ul>
                        </div>

                        <div>
                            <Typography variant="h3" className="mb-2">
                                Challenges & Solutions
                            </Typography>
                            <p className="text-muted-foreground mt-2">
                                I'll write more details here about the technical challenges faced during this project
                                and how I solved them.
                            </p>
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="live">
                    <div className="flex flex-col items-center space-y-6">
                        <Typography variant="h3">Visit Live Project</Typography>
                        <p className="text-center max-w-2xl">
                            The project is live and you can interact with it directly by clicking the button below.
                        </p>
                        <NextLink href={project.status.link || "#"} target="_blank" passHref>
                            <Button size="lg" className="mt-4">
                                <span className="icon-[tabler--external-link] size-5 mr-2" />
                                Visit Project
                            </Button>
                        </NextLink>
                    </div>
                </TabsContent>
            </Tabs>

            {otherProjects.length > 0 && (
                <div className="mt-16">
                    <Typography variant="h2" className="mb-6">
                        Other Projects
                    </Typography>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {otherProjects.map((p) => (
                            <NextLink key={p.id} href={`/projects/${p.id}`} passHref>
                                <Card className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow">
                                    <div className="aspect-video relative">
                                        <Image
                                            src={`https://web-mediafraucloud-com.s3.amazonaws.com/media/projects/${p.id}_screenshot_${p.featuredScreenshotIndex}_square.webp`}
                                            alt={p.title}
                                            fill
                                            className="object-cover"
                                        />
                                    </div>
                                    <CardContent className="p-4">
                                        <Typography variant="h4">{p.title}</Typography>
                                    </CardContent>
                                </Card>
                            </NextLink>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
